import { useConvex } from "convex/react";
import {
  AlertCircle,
  ArrowLeft,
  Crown,
  Palette,
  Server,
  Settings,
  Trash2,
  Upload,
  UserMinus,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { S3ConnectionDialog } from "@/components/s3/S3ConnectionDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { StorageService } from "@/services/storageService";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import {
  createDefaultCraftspaceIfMissing as createLocalCraftspaceIfMissing,
  removeCraftspace as removeLocalCraftspace,
  updateCraftspace as updateLocalCraftspace,
  useCraftspace as useLocalCraftspace,
  useProjectsByCraftspace as useLocalProjectsByCraftspace,
} from "@/data";
import type { Tables } from "@/types/database";
import { getCraftspaceFavicon, isUrl } from "@/utils/faviconUtils";
import { api } from "../../convex/_generated/api";

type Craftspace = Tables<"craftspaces">;
type TeamMember = {
  user_id: string;
  email: string;
  name: string;
  role: "owner" | "editor";
  is_owner: boolean;
};

type CraftspaceS3Connection = {
  name?: string;
  provider?: string;
  bucket_name?: string;
  region?: string;
  endpoint?: string;
};

const CraftspaceSettings = () => {
  const { craftspaceId } = useParams<{ craftspaceId: string }>();
  const convex = useConvex();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const localCraftspace = useLocalCraftspace(craftspaceId);
  const isLocalMode = isElectron() && (!user || !!localCraftspace);
  const localProjects = useLocalProjectsByCraftspace(craftspaceId);

  // Helper function to get craftspace initials
  const getCraftspaceInitials = (name: string): string => {
    if (!name) return "WS";

    // If the name is 2 characters or less, use the whole name
    if (name.length <= 2) {
      return name.toUpperCase();
    }

    // If the name has spaces, take first character of each word
    if (name.includes(" ")) {
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }

    // If the name is longer than 2 characters and has no spaces, take first 2 characters
    return name.toUpperCase().slice(0, 2);
  };

  // Handle custom icon upload
  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select an image file (PNG, JPG, GIF, etc.)",
      });
      return;
    }

    // No file size limit needed - we compress everything to 64x64 anyway

    setUploadingIcon(true);
    setCustomIconFile(file);

    try {
      // Compress the image for craftspace icon
      console.log("Compressing image...");
      const compressedFile = await compressImage(file, 256, 256, 0.9);

      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(compressedFile);
      setCustomIcon(previewUrl);

      // Upload the compressed file to storage
      const fileName = `${craftspace?.id ?? craftspaceId}/craftspace-icon.jpg`; // Always use jpg after compression

      await StorageService.uploadPrivate("staging", fileName, compressedFile, { upsert: true });

      // Get the signed URL (more reliable than public URL)
      const { data: signedUrlData } = await StorageService.getPrivateUrl(
        "staging",
        fileName,
        60 * 60 * 24 * 365,
      );

      const iconUrl = signedUrlData.signedUrl;
      console.log("Created signed URL:", iconUrl);

      // Update the craftspace with the new icon URL
      console.log("Updating craftspace with icon URL:", iconUrl);
      if (!craftspace) {
        throw new Error("Craftspace not loaded");
      }
      await convex.mutation(api.craftspaces.update, {
        id: craftspace.id,
        icon_url: iconUrl,
      });
      console.log("Database updated successfully");

      // Update local state with the actual uploaded URL
      console.log("Upload successful, signed URL:", iconUrl);
      if (craftspace) {
        const updatedCraftspace = { ...craftspace, icon_url: iconUrl };
        setCraftspace(updatedCraftspace);
        console.log("Updated craftspace state:", updatedCraftspace);
      }

      toast({
        title: "Icon updated!",
        description: "Your craftspace icon has been updated successfully.",
      });
    } catch (error) {
      console.error("Error uploading icon:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "Failed to upload craftspace icon. Please try again.",
      });
      // Reset to previous state
      setCustomIcon(null);
    } finally {
      setUploadingIcon(false);
    }
  };

  // Handle custom icon removal
  const handleIconRemove = async () => {
    if (!craftspace) return;

    setUploadingIcon(true);

    try {
      // Check if craftspace name is a URL and try to fetch favicon
      let newIconUrl: string | null = null;
      const currentCraftspaceName = craftspaceName || craftspace?.name || "";

      if (isUrl(currentCraftspaceName)) {
        console.log("Craftspace name is a URL, attempting to fetch favicon...");
        try {
          newIconUrl = await getCraftspaceFavicon(currentCraftspaceName);
          console.log("Fetched favicon:", newIconUrl);
        } catch (error) {
          console.warn("Failed to fetch favicon after icon removal:", error);
          // Continue with null - will fall back to initials
        }
      }

      // Update the craftspace with the new icon URL (favicon or null)
      await convex.mutation(api.craftspaces.update, {
        id: craftspace.id,
        icon_url: newIconUrl ?? undefined,
      });

      // Update local state
      setCustomIcon(null);
      setCustomIconFile(null);
      if (craftspace) {
        setCraftspace({ ...craftspace, icon_url: newIconUrl });
      }

      toast({
        title: "Icon removed!",
        description: newIconUrl
          ? "Custom icon removed, favicon restored from domain."
          : "Custom icon removed.",
      });
    } catch (error) {
      console.error("Error removing icon:", error);
      toast({
        variant: "destructive",
        title: "Remove failed",
        description: "Failed to remove craftspace icon. Please try again.",
      });
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleRevealCraftspaceFolder = async () => {
    if (!craftspace) return;
    try {
      await window.electronAPI?.revealCraftspaceFolder(craftspace.id);
    } catch (error) {
      console.error("Failed to reveal craftspace folder:", error);
      toast({
        variant: "destructive",
        title: "Reveal failed",
        description: "Could not open the craftspace folder.",
      });
    }
  };

  const [craftspace, setCraftspace] = useState<Craftspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteCraftspaceName, setDeleteCraftspaceName] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const [creatingDefaultCraftspace, setCreatingDefaultCraftspace] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [leavingCraftspace, setLeavingCraftspace] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitingMember, setInvitingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [s3ConnectionDialogOpen, setS3ConnectionDialogOpen] = useState(false);
  const [s3Connection, setS3Connection] = useState<CraftspaceS3Connection | null>(null);
  const [loadingS3Connection, setLoadingS3Connection] = useState(false);

  // Form state
  const [craftspaceName, setCraftspaceName] = useState("");
  const [craftspaceSlugInput, setCraftspaceSlugInput] = useState("");
  const [_customIcon, setCustomIcon] = useState<string | null>(null);
  const [_customIconFile, setCustomIconFile] = useState<File | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [isFetchingFavicon, setIsFetchingFavicon] = useState(false);

  // Compress image for craftspace icon
  const compressImage = (
    file: File,
    maxWidth: number = 256,
    maxHeight: number = 256,
    quality: number = 0.9,
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              console.log(
                `Compressed image: ${file.size} bytes → ${compressedFile.size} bytes (${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`,
              );
              resolve(compressedFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg",
          quality,
        );
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle craftspace name changes for favicon fetching
  const handleCraftspaceNameChange = async (value: string) => {
    setCraftspaceName(value);
    setFaviconPreview(null);

    if (isUrl(value)) {
      setIsFetchingFavicon(true);
      try {
        const favicon = await getCraftspaceFavicon(value);
        setFaviconPreview(favicon);
      } catch (error) {
        console.warn("Failed to fetch favicon preview:", error);
      } finally {
        setIsFetchingFavicon(false);
      }
    }
  };

  const createDefaultCraftspace = useCallback(async () => {
    if (!user) return;

    setCreatingDefaultCraftspace(true);
    try {
      const ensuredCraftspace = await convex.mutation(
        api.craftspaces.createDefaultCraftspaceIfMissing,
        {},
      );

      if (ensuredCraftspace?.id && ensuredCraftspace.id !== craftspaceId) {
        navigate(`/craftspace/${ensuredCraftspace.id}/settings`, { replace: true });
        return;
      }
    } catch (error) {
      console.error("Error in createDefaultCraftspace:", error);
    } finally {
      setCreatingDefaultCraftspace(false);
    }
  }, [convex, navigate, user, craftspaceId]);

  const fetchCraftspaceMembers = useCallback(
    async (targetCraftspaceId: string) => {
      try {
        const memberList = await convex.query(api.craftspaces.listMembers, {
          craftspaceId: targetCraftspaceId,
        });
        setMembers((memberList || []) as TeamMember[]);
      } catch (error) {
        console.error("Error fetching craftspace members:", error);
        setMembers([]);
      }
    },
    [convex],
  );

  const fetchS3Connection = useCallback(
    async (targetCraftspaceId: string) => {
      setLoadingS3Connection(true);
      try {
        const connection = await convex.query(api.s3Connections.getByCraftspace, {
          craftspaceId: targetCraftspaceId,
        });
        setS3Connection((connection || null) as CraftspaceS3Connection | null);
      } catch (error) {
        console.error("Error fetching S3 connection:", error);
        setS3Connection(null);
      } finally {
        setLoadingS3Connection(false);
      }
    },
    [convex],
  );

  const fetchCraftspaceData = useCallback(async () => {
    if (!craftspaceId || !user) {
      setLoading(false);
      return;
    }

    if (isLocalMode) {
      return;
    }

    // In Electron local mode, wait for local craftspace resolution before hitting Convex.
    if (isElectron() && localCraftspace === undefined) {
      return;
    }

    setLoading(true);
    try {
      const craftspaceData = await convex.query(api.craftspaces.get, { id: craftspaceId });

      if (!craftspaceData) {
        setCraftspace(null);
        setIsOwner(false);
        setProjectCount(0);
        setMembers([]);
        setS3Connection(null);
        return;
      }

      setCraftspace(craftspaceData as Craftspace);
      setCraftspaceSlugInput(craftspaceData.slug || "");
      setIsOwner(craftspaceData.owner_id === user.id);
      await fetchCraftspaceMembers(craftspaceData.id || craftspaceId);
      await fetchS3Connection(craftspaceData.id || craftspaceId);

      const projects = await convex.query(api.projects.listByCraftspace, { craftspaceId });
      setProjectCount((projects || []).length);
    } catch (error) {
      console.error("Error fetching craftspace data:", error);
      setCraftspace(null);
      setIsOwner(false);
      setProjectCount(0);
      setS3Connection(null);
    } finally {
      setLoading(false);
    }
  }, [
    convex,
    fetchS3Connection,
    fetchCraftspaceMembers,
    isLocalMode,
    localCraftspace,
    user,
    craftspaceId,
  ]);

  useEffect(() => {
    if (craftspaceId) {
      fetchCraftspaceData();
    }
  }, [craftspaceId, fetchCraftspaceData]);

  useEffect(() => {
    if (!isLocalMode) return;

    if (localCraftspace === undefined) {
      return;
    }

    if (localCraftspace) {
      setCraftspace({
        ...localCraftspace,
        id: localCraftspace._id,
        owner_id: "local",
      } as Craftspace);
      setIsOwner(true);
      setMembers([]);
      setS3Connection(null);
      setProjectCount(0);
      setLoading(false);
      return;
    }

    const ensureLocalCraftspace = async () => {
      try {
        const ensuredId = await createLocalCraftspaceIfMissing();
        if (ensuredId && ensuredId !== craftspaceId) {
          navigate(`/craftspace/${ensuredId}/settings`, { replace: true });
        }
      } catch (error) {
        console.error("Error creating local craftspace:", error);
      } finally {
        setLoading(false);
      }
    };

    void ensureLocalCraftspace();
  }, [isLocalMode, localCraftspace, navigate, craftspaceId]);

  // If no craftspace exists, create a default one (not in Electron where local craftspaces are used)
  useEffect(() => {
    if (user && !loading && !craftspace && !creatingDefaultCraftspace && !isElectron()) {
      createDefaultCraftspace();
    }
  }, [user, loading, craftspace, creatingDefaultCraftspace, createDefaultCraftspace]);

  // Update craftspaceName when craftspace data changes
  useEffect(() => {
    if (craftspace) {
      setCraftspaceName(craftspace.name);
      setCraftspaceSlugInput(craftspace.slug || "");
    }
  }, [craftspace]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated (Electron local mode bypasses auth)
  if (!user && !isElectron()) {
    return <Navigate to="/login" replace />;
  }

  const handleSave = async () => {
    if (!craftspace) return;

    setSaving(true);
    try {
      // Check if the new craftspace name is a URL and fetch favicon
      let newIconUrl = craftspace.icon_url; // Keep existing icon by default

      if (isUrl(craftspaceName)) {
        console.log("Craftspace name is a URL, fetching favicon...");
        try {
          const favicon = await getCraftspaceFavicon(craftspaceName);
          if (favicon) {
            newIconUrl = favicon;
            console.log("Fetched favicon for craftspace:", favicon);
          }
        } catch (error) {
          console.warn("Failed to fetch favicon for craftspace name:", error);
          // Continue with existing icon if favicon fetch fails
        }
      }

      if (isLocalMode) {
        await updateLocalCraftspace({
          id: craftspace.id as string,
          name: craftspaceName,
          icon_url: newIconUrl ?? undefined,
        });
      } else {
        await convex.mutation(api.craftspaces.update, {
          id: craftspace.id,
          name: craftspaceName,
          icon_url: newIconUrl ?? undefined,
        });
      }

      setCraftspace((prev) =>
        prev ? { ...prev, name: craftspaceName, icon_url: newIconUrl } : null,
      );

      toast({
        title: "Settings saved",
        description:
          isUrl(craftspaceName) && newIconUrl !== craftspace.icon_url
            ? "Craftspace name and favicon have been updated."
            : "Your craftspace settings have been updated.",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSlug = async () => {
    if (!craftspace || !craftspaceSlugInput.trim()) return;
    setSaving(true);
    try {
      if (isLocalMode) {
        await updateLocalCraftspace({
          id: craftspace.id as string,
          slug: craftspaceSlugInput,
        });
        setCraftspace((prev) => (prev ? { ...prev, slug: craftspaceSlugInput } : prev));
      } else {
        const updated = await convex.mutation(api.craftspaces.setSlug, {
          id: craftspace.id,
          slug: craftspaceSlugInput,
        });
        setCraftspace((prev) => (prev ? { ...prev, slug: updated.slug } : prev));
      }
      toast({
        title: "Slug saved",
        description: "Craftspace slug has been set.",
      });
    } catch (error) {
      console.error("Error saving slug:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to save craftspace slug",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCraftspace = async () => {
    if (!craftspace || deleteCraftspaceName !== craftspace.name) return;

    // Check if craftspace has projects
    const localProjectsLoaded = localProjects !== undefined;
    const effectiveProjectCount = isLocalMode
      ? (localProjects?.length ?? 0)
      : projectCount;

    if (isLocalMode && !localProjectsLoaded) {
      toast({
        variant: "destructive",
        title: "Please wait",
        description: "Still loading local projects. Try again in a moment.",
      });
      return;
    }

    if (effectiveProjectCount > 0) {
      toast({
        variant: "destructive",
        title: "Cannot Delete Craftspace",
        description: `This craftspace contains ${effectiveProjectCount} project${effectiveProjectCount !== 1 ? "s" : ""}. Please delete all projects before deleting the craftspace.`,
      });
      return;
    }

    try {
      if (isLocalMode) {
        await removeLocalCraftspace(craftspace.id as string);
      } else {
        await convex.mutation(api.craftspaces.remove, { id: craftspace.id });
      }

      toast({
        title: "Craftspace deleted",
        description: "Your craftspace has been permanently deleted.",
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Error deleting craftspace:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete craftspace",
      });
    }
  };

  const handleInviteMember = async () => {
    if (!craftspace || !isOwner) return;

    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Enter an email address to share this craftspace.",
      });
      return;
    }

    setInvitingMember(true);
    try {
      const result = await convex.mutation(api.craftspaces.addMemberByEmail, {
        craftspaceId: craftspace.id,
        email,
        role: "editor",
      });

      await fetchCraftspaceMembers(craftspace.id);
      setInviteEmail("");

      toast({
        title: result?.already_member ? "Already shared" : "Craftspace shared",
        description: result?.already_member
          ? `${email} already has access to this craftspace.`
          : `${email} can now access this craftspace.`,
      });
    } catch (error) {
      console.error("Error inviting craftspace member:", error);
      toast({
        variant: "destructive",
        title: "Failed to share craftspace",
        description:
          error instanceof Error ? error.message : "Unable to share craftspace right now.",
      });
    } finally {
      setInvitingMember(false);
    }
  };

  const handleRemoveMember = async (memberUserId: string) => {
    if (!craftspace || !isOwner) return;

    setRemovingMemberId(memberUserId);
    try {
      await convex.mutation(api.craftspaces.removeMember, {
        craftspaceId: craftspace.id,
        memberUserId,
      });

      await fetchCraftspaceMembers(craftspace.id);
      toast({
        title: "Member removed",
        description: "Craftspace access has been revoked.",
      });
    } catch (error) {
      console.error("Error removing craftspace member:", error);
      toast({
        variant: "destructive",
        title: "Failed to remove member",
        description: error instanceof Error ? error.message : "Unable to remove member right now.",
      });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleLeaveCraftspace = async () => {
    if (!craftspace || !user) return;

    setLeavingCraftspace(true);
    try {
      await convex.mutation(api.craftspaces.leaveCraftspace, {
        craftspaceId: craftspace.id,
      });

      toast({
        title: "Left craftspace",
        description: "You no longer have access to this craftspace.",
      });
      navigate("/dashboard");
    } catch (error) {
      console.error("Error leaving craftspace:", error);
      toast({
        variant: "destructive",
        title: "Failed to leave craftspace",
        description: error instanceof Error ? error.message : "Unable to leave craftspace.",
      });
    } finally {
      setLeavingCraftspace(false);
    }
  };

  const handleS3ConnectionComplete = async (connectionData: {
    craftspaceName: string;
    provider: string;
    bucketName: string;
    region: string;
    endpoint: string;
  }) => {
    if (!craftspace || !isOwner) {
      return;
    }

    try {
      await convex.mutation(api.craftspaces.update, {
        id: craftspace.id,
        storage_provider: "custom-s3",
        s3_bucket_name: connectionData.bucketName,
        s3_region: connectionData.region,
        s3_endpoint: connectionData.endpoint,
      });

      await fetchCraftspaceData();
      await fetchS3Connection(craftspace.id);
      setS3ConnectionDialogOpen(false);

      toast({
        title: "S3 credentials updated",
        description: "Craftspace S3 credentials were saved successfully.",
      });
    } catch (error) {
      console.error("Error updating craftspace S3 settings:", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Failed to update craftspace S3 settings.",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="mt-2 text-white">Loading craftspace settings...</p>
        </div>
      </div>
    );
  }

  // Don't redirect if craftspace is null - let the useEffect handle creating a default craftspace
  if (!craftspace && !loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="mt-2 text-white">Setting up your craftspace...</p>
        </div>
      </div>
    );
  }

  // Don't render the main content if craftspace is not loaded yet
  if (!craftspace) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-gray-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Craftspace Settings</h1>
                <p className="text-gray-300">Manage your craftspace and team members</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto">
          <nav className="flex space-x-8">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white border-b-2 border-blue-600"
            >
              <Settings className="w-4 h-4 mr-2" />
              General
            </Button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Craftspace Information */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Craftspace Information
              </CardTitle>
              <CardDescription className="text-gray-400">
                Basic craftspace details and settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="craftspaceId" className="text-gray-300">
                  Craftspace ID
                </Label>
                <Input
                  id="craftspaceId"
                  value={craftspace.id}
                  readOnly
                  className="bg-gray-700 border-gray-600 text-gray-300 font-mono text-sm"
                />
                {isLocalMode && (
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleRevealCraftspaceFolder}
                      className="bg-gray-700 hover:bg-gray-600 text-gray-200"
                    >
                      Reveal Craftspace Folder
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="craftspaceSlug" className="text-gray-300">
                  Craftspace Slug
                </Label>
                {craftspace.slug ? (
                  <Input
                    id="craftspaceSlug"
                    value={craftspace.slug || ""}
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300 font-mono text-sm"
                    placeholder="craftspace"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      id="craftspaceSlug"
                      value={craftspaceSlugInput}
                      onChange={(e) => setCraftspaceSlugInput(e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                      placeholder="craftspace"
                    />
                    <Button
                      type="button"
                      onClick={handleSaveSlug}
                      disabled={!craftspaceSlugInput.trim() || saving}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="craftspaceName" className="text-gray-300">
                  Craftspace Name
                </Label>
                <Input
                  id="craftspaceName"
                  value={craftspaceName}
                  onChange={(e) => handleCraftspaceNameChange(e.target.value)}
                  onBlur={handleSave}
                  disabled={!isOwner || saving}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Enter craftspace name or domain (e.g., google.com)"
                />
              </div>

              {/* Craftspace Icon Preview */}
              <div>
                <Label className="text-gray-300">Craftspace Icon</Label>
                <div className="flex items-center space-x-4 p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-gray-600 border border-gray-500">
                    {craftspace?.icon_url || faviconPreview ? (
                      <img
                        src={craftspace?.icon_url || faviconPreview}
                        alt={`${craftspaceName || craftspace?.name || "Craftspace"} icon`}
                        className="w-8 h-8 rounded-sm object-cover"
                        onLoad={() => console.log("Icon loaded successfully")}
                        onError={(e) => {
                          console.log(
                            "Icon failed to load:",
                            craftspace?.icon_url || faviconPreview,
                          );
                          // Hide the image and let the fallback show
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    {!craftspace?.icon_url && !faviconPreview && (
                      <span className="text-lg font-semibold text-white">
                        {getCraftspaceInitials(craftspaceName || craftspace?.name || "Craftspace")}
                      </span>
                    )}
                    {isFetchingFavicon && (
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">
                      {isFetchingFavicon ? (
                        <>
                          <span className="text-blue-400">⟳</span>
                          <span className="text-white">Fetching favicon...</span>
                        </>
                      ) : craftspace?.icon_url ? (
                        <>
                          <span className="text-green-400">✓</span>
                          {isUrl(craftspaceName || craftspace?.name || "")
                            ? "Using favicon from domain"
                            : "Using custom icon"}
                          {isUrl(craftspaceName || craftspace?.name || "") && (
                            <span className="text-gray-400 ml-2">
                              ({craftspaceName || craftspace?.name})
                            </span>
                          )}
                        </>
                      ) : faviconPreview ? (
                        <>
                          <span className="text-green-400">✓</span>
                          <span className="text-white">Using favicon preview</span>
                          <span className="text-gray-400 ml-2">({craftspaceName})</span>
                        </>
                      ) : (
                        <>
                          <span className="text-gray-400">•</span> Using initials
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      This is how your craftspace appears in the sidebar
                    </p>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleIconUpload}
                      disabled={!isOwner || uploadingIcon}
                      className="hidden"
                      id="icon-upload"
                    />
                    <label
                      htmlFor="icon-upload"
                      className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg border transition-colors cursor-pointer ${
                        !isOwner || uploadingIcon
                          ? "bg-gray-600 border-gray-500 text-gray-400 cursor-not-allowed"
                          : "bg-gray-600 border-gray-500 text-white hover:bg-gray-500 hover:border-gray-400"
                      }`}
                    >
                      <Upload size={16} />
                      <span>{uploadingIcon ? "Uploading..." : "Upload"}</span>
                    </label>
                    {craftspace?.icon_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleIconRemove}
                        disabled={!isOwner || uploadingIcon}
                        className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                      >
                        <X size={16} className="mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* S3 Storage Credentials */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Server className="w-5 h-5 mr-2" />
                S3 Storage Credentials
              </CardTitle>
              <CardDescription className="text-gray-400">
                Manage custom S3 storage access for this craftspace.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Provider</Label>
                  <Input
                    value={s3Connection?.provider || "Not configured"}
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Bucket Name</Label>
                  <Input
                    value={
                      s3Connection?.bucket_name ||
                      (craftspace.s3_bucket_name as string) ||
                      "Not configured"
                    }
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Region</Label>
                  <Input
                    value={
                      s3Connection?.region || (craftspace.s3_region as string) || "Not configured"
                    }
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Endpoint</Label>
                  <Input
                    value={
                      s3Connection?.endpoint ||
                      (craftspace.s3_endpoint as string) ||
                      "Not configured"
                    }
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300"
                  />
                </div>
              </div>

              {loadingS3Connection && (
                <p className="text-sm text-gray-400">Loading S3 connection details...</p>
              )}

              {isOwner ? (
                <div className="flex justify-end">
                  <Button
                    onClick={() => setS3ConnectionDialogOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {s3Connection ? "Edit S3 Credentials" : "Configure S3 Credentials"}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  Only craftspace owners can view and update S3 credentials.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Team Members
              </CardTitle>
              <CardDescription className="text-gray-400">
                Share this craftspace with other users.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isOwner ? (
                <div className="flex gap-2">
                  <Input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter user email"
                    className="bg-gray-700 border-gray-600 text-white"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleInviteMember();
                      }
                    }}
                  />
                  <Button
                    onClick={handleInviteMember}
                    disabled={invitingMember}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {invitingMember ? "Sharing..." : "Share"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-gray-400">
                    Only the craftspace owner can manage team members.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-500/40 text-red-300 hover:bg-red-600 hover:text-white"
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        Leave Craftspace
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-gray-800 border-gray-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                          Leave this craftspace?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          You will lose access to all projects in this craftspace.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-600 text-white hover:bg-red-700"
                          onClick={handleLeaveCraftspace}
                          disabled={leavingCraftspace}
                        >
                          {leavingCraftspace ? "Leaving..." : "Leave"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              <div className="space-y-2">
                {members.length === 0 ? (
                  <p className="text-sm text-gray-400">No team members yet.</p>
                ) : (
                  members.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-700 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-white truncate">
                            {member.name || member.email}
                          </p>
                          <span className="inline-flex items-center text-xs text-gray-300 bg-gray-700 px-2 py-0.5 rounded">
                            {member.is_owner ? (
                              <>
                                <Crown className="w-3 h-3 mr-1 text-yellow-400" />
                                Owner
                              </>
                            ) : (
                              "Editor"
                            )}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">{member.email}</p>
                      </div>
                      {isOwner && !member.is_owner && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveMember(member.user_id)}
                          disabled={removingMemberId === member.user_id}
                          className="border-red-500/40 text-red-300 hover:bg-red-600 hover:text-white"
                        >
                          <UserMinus className="w-4 h-4 mr-1" />
                          {removingMemberId === member.user_id ? "Removing..." : "Remove"}
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          {isOwner && (
            <Card className="bg-gray-800 border-red-600">
              <CardHeader>
                <CardTitle className="text-red-400">Danger Zone</CardTitle>
                <CardDescription className="text-gray-400">
                  Irreversible and destructive actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Project Count Warning */}
                  {projectCount > 0 && (
                    <div className="bg-yellow-900/20 border border-yellow-500/30 p-3 rounded">
                      <h5 className="text-yellow-200 font-medium mb-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Craftspace Contains Projects
                      </h5>
                      <p className="text-yellow-100 text-sm">
                        This craftspace contains{" "}
                        <strong>
                          {projectCount} project{projectCount !== 1 ? "s" : ""}
                        </strong>
                        . You must delete all projects before you can delete the craftspace.
                      </p>
                    </div>
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={projectCount > 0}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Craftspace
                        {projectCount > 0 &&
                          ` (${projectCount} project${projectCount !== 1 ? "s" : ""} remaining)`}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-gray-800 border-gray-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Craftspace</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          This action cannot be undone. This will permanently delete the craftspace "
                          {craftspace.name}". All projects must be deleted before the craftspace can
                          be removed. Please type the craftspace name to confirm deletion.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Input
                        value={deleteCraftspaceName}
                        onChange={(e) => setDeleteCraftspaceName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && deleteCraftspaceName === craftspace.name) {
                            handleDeleteCraftspace();
                          }
                        }}
                        placeholder={`Type "${craftspace.name}" to confirm`}
                        className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                      />
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteCraftspace}
                          disabled={deleteCraftspaceName !== craftspace.name}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete Craftspace
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <S3ConnectionDialog
        open={s3ConnectionDialogOpen}
        onClose={() => setS3ConnectionDialogOpen(false)}
        craftspaceId={craftspace.id}
        craftspaceName={craftspace.name}
        initialConnection={{
          provider: s3Connection?.provider,
          bucketName: s3Connection?.bucket_name || (craftspace.s3_bucket_name as string),
          region: s3Connection?.region || (craftspace.s3_region as string),
          endpoint: s3Connection?.endpoint || (craftspace.s3_endpoint as string),
          connectionName: s3Connection?.name,
        }}
        onConnectionComplete={handleS3ConnectionComplete}
      />
    </div>
  );
};

export default CraftspaceSettings;
